import React, { useState, useEffect } from 'react';
import { normalizeGoogleEventId } from '../../lib/utils/googleEventId';
import { ResponsiveModal } from '../ui/ResponsiveModal';
import { MobileDrawer } from '../ui/MobileDrawer';
import { Button } from '../ui';
import { X, Calendar, User, AlertCircle, ExternalLink } from 'lucide-react';
import { useUserStore } from '../../lib/stores/userStore';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/auth';
import { useApp } from '../AppProvider';
import { useGoogleCalendar } from '../../hooks/deals/useGoogleCalendar';
import { useMicrosoftCalendar } from '../../hooks/deals/useMicrosoftCalendar';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { useActivityTypesStore, getActivityTypeLabel } from '../../lib/stores/activityTypesStore';
import { useBookingRule } from '../../lib/calendar/useBookingRule';
import { bookingError, filterBookingOptions } from '../../lib/calendar/booking';

interface QuickTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTaskCreated?: () => void;
  translations?: any;
}

export function QuickTaskModal({ isOpen, onClose, onTaskCreated, translations = {} }: QuickTaskModalProps) {
  const { user } = useAuth();
  const { settings } = useSettingsStore();
  const { users, fetchUsers } = useUserStore();
  const { toast } = useApp();
  const { createEvent } = useGoogleCalendar();
  const { createEvent: createMicrosoftEvent } = useMicrosoftCalendar();

  // Tipi attività custom (per-org) — mostrati in coda alle opzioni di sistema
  // nella select "Tipo" (stesso pattern di DealActivityForm.tsx / LeadStatusUpdateDrawer.tsx)
  const { customTypes, fetched: customTypesFetched, fetchCustomTypes } = useActivityTypesStore();
  useEffect(() => {
    if (!customTypesFetched) fetchCustomTypes();
  }, [customTypesFetched, fetchCustomTypes]);
  const activeCustomTypes = customTypes.filter(ct => ct.active);

  // Activity settings con fallback per retrocompatibilità
  const activityDefaultType = (settings?.activity_default_type || 'task') as 'call' | 'meeting' | 'email' | 'task' | 'videochiamata' | 'appuntamento' | 'sopralluogo' | 'other';
  const activityTitleRequired = settings?.activity_title_required !== false; // true se undefined/null/true

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: '',
    due_date: '',
    due_time: '09:00',
    type: 'task' as 'call' | 'meeting' | 'email' | 'task' | 'videochiamata' | 'appuntamento' | 'sopralluogo' | 'other'
  });

  // Regola "Fissare gli appuntamenti" (Impostazioni › Calendario): qui non c'e' un lead, quindi
  // "proprietario del lead" parte vuoto. Quando inizia a valere il campo prende il valore della regola.
  const booking = useBookingRule(isOpen ? formData.type : null, null);
  const [bookingApplied, setBookingApplied] = useState(false);
  useEffect(() => {
    if (!booking) { setBookingApplied(false); return; }
    if (!bookingApplied) {
      setBookingApplied(true);
      setFormData(prev => ({ ...prev, assigned_to: booking.startId || '' }));
    } else {
      setFormData(prev => (prev.assigned_to && !booking.allowedIds.has(prev.assigned_to) ? { ...prev, assigned_to: booking.startId || '' } : prev));
    }
  }, [booking]); // eslint-disable-line react-hooks/exhaustive-deps
  const assigneeError = bookingError(booking, formData.assigned_to);

  const [loading, setLoading] = useState(false);
  const [shouldSyncToGoogleCalendar, setShouldSyncToGoogleCalendar] = useState(false);
  const [activitiesCalendarId, setActivitiesCalendarId] = useState<string | null>(null);
  // Microsoft (Outlook) sync — speculare a Google, sync indipendente
  const [microsoftSyncEnabled, setMicrosoftSyncEnabled] = useState(false);
  const [microsoftCalendarId, setMicrosoftCalendarId] = useState<string | null>(null);
  const [microsoftConnected, setMicrosoftConnected] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen && users.length === 0) {
      fetchUsers();
    }
  }, [isOpen, users.length, fetchUsers]);

  // Set default activity type from settings when modal opens
  useEffect(() => {
    if (isOpen) {
      const dbValidTypes = ['call', 'videochiamata', 'appuntamento', 'sopralluogo', 'email', 'task', 'note', 'other'];
      const effectiveDefault = activityDefaultType === 'meeting' ? 'videochiamata' : activityDefaultType;
      const safeDefault = dbValidTypes.includes(effectiveDefault) ? effectiveDefault : 'task';

      setFormData(prev => ({
        ...prev,
        type: safeDefault as 'call' | 'email' | 'task' | 'videochiamata' | 'appuntamento' | 'sopralluogo' | 'other'
      }));
    }
  }, [isOpen, activityDefaultType]);

  // Load assigned user's Google Calendar sync settings
  useEffect(() => {
    const loadAssignedUserSyncSettings = async () => {
      if (!user) return;

      const targetUserId = formData.assigned_to || user.id;

      try {
        // Impostazioni Google + Microsoft dell'ASSEGNATARIO (una sola query).
        // La edge microsoft-calendar-modify riceve target_user_id = assegnatario.
        const { data: assignedUserData, error: assignedUserError } = await supabase
          .from('user_profiles')
          .select('sync_activities, sync_activities_calendar_id, microsoft_account_connected, microsoft_sync_activities, microsoft_sync_activities_calendar_id')
          .eq('id', targetUserId)
          .single();

        if (!assignedUserError && assignedUserData) {
          setShouldSyncToGoogleCalendar(assignedUserData.sync_activities || false);
          setActivitiesCalendarId(assignedUserData.sync_activities_calendar_id || null);
          setMicrosoftConnected(!!(assignedUserData as any).microsoft_account_connected);
          setMicrosoftSyncEnabled(!!(assignedUserData as any).microsoft_sync_activities);
          setMicrosoftCalendarId((assignedUserData as any).microsoft_sync_activities_calendar_id || null);
        }
      } catch (err) {
        console.error('Error loading assigned user sync settings:', err);
      }
    };

    if (isOpen) {
      loadAssignedUserSyncSettings();
    }
  }, [formData.assigned_to, user, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate title (solo se obbligatorio nelle settings)
    if (!formData.title.trim()) {
      if (activityTitleRequired) {
        toast.error('Il titolo della task è obbligatorio');
        return;
      } else {
        // Auto-popola il titolo con il tipo tradotto
        const typeLabels: Record<string, string> = {
          call: 'Chiamata',
          meeting: 'Incontro',
          email: 'Email',
          task: 'Task',
          videochiamata: 'Videochiamata',
          appuntamento: 'Appuntamento',
          sopralluogo: 'Sopralluogo',
          other: 'Altro'
        };
        formData.title = typeLabels[formData.type] || getActivityTypeLabel(formData.type, customTypes);
      }
    }

    if (!user) {
      toast.error('Utente non autenticato');
      return;
    }

    if (assigneeError) {
      toast.error(assigneeError);
      return;
    }

    setLoading(true);
    try {
      // Get user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found');
      }

      // Combine date and time for due_date
      let combinedDueDate: string | undefined;
      if (formData.due_date && formData.due_time) {
        const dateTime = new Date(`${formData.due_date}T${formData.due_time}`);
        combinedDueDate = dateTime.toISOString();
      }

      // Create the sales activity
      const activityData = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        type: formData.type,
        status: 'pending',
        due_date: combinedDueDate,
        user_id: userData.user.id, // Creator of the activity
        assigned_to: formData.assigned_to || userData.user.id, // User assigned to complete the activity
        organization_id: profileData.organization_id
        // Note: deal_id is null for standalone tasks
      };

      const { data, error } = await supabase
        .from('sales_activities')
        .insert(activityData)
        .select()
        .single();

      if (error) throw error;

      // Emit workflow event
      try {
        const { eventBus } = await import('../../lib/events/eventBus');
        const leadId = data?.client_id || null;
        if (leadId) {
          eventBus.emitEvent('lead:activity_created' as any, {
            leadId: leadId,
            activityId: data.id,
            activityType: data.activity_type || data.type,
            activityTitle: data.title || data.description,
          });
        }
      } catch (e) { /* ignore event emission errors */ }

      // Sync to Google Calendar if enabled
      if (shouldSyncToGoogleCalendar && activitiesCalendarId && data && combinedDueDate) {
        try {
          const endTime = new Date(new Date(combinedDueDate).getTime() + 60 * 60 * 1000); // +1 hour
          const eventData = {
            title: formData.title,
            description: formData.description,
            start_time: combinedDueDate,
            end_time: endTime.toISOString(),
            location: '',
            color: '#3B82F6'
          };

          const targetUserId = formData.assigned_to || userData.user.id;
          const createdEvent = await createEvent(
            activitiesCalendarId,
            eventData,
            targetUserId
          );

          if (createdEvent) {
            // Update activity with Google Calendar event ID
            await supabase
              .from('sales_activities')
              .update({ google_event_id: normalizeGoogleEventId(createdEvent.id) })
              .eq('id', data.id);
          }
        } catch (err) {
          console.error('Failed to sync with Google Calendar:', err);
          // Don't fail the whole operation if calendar sync fails
        }
      }

      // Sync to Microsoft (Outlook) Calendar if enabled — INDIPENDENTE dal push Google
      // Evento sul calendario dell'ASSEGNATARIO (target_user_id via edge, F1B).
      if (microsoftConnected && microsoftSyncEnabled && microsoftCalendarId && data && combinedDueDate) {
        try {
          const endTime = new Date(new Date(combinedDueDate).getTime() + 60 * 60 * 1000); // +1 hour
          const msTargetUserId = formData.assigned_to || userData.user.id;
          const msResult = await createMicrosoftEvent(microsoftCalendarId, {
            title: formData.title,
            description: formData.description,
            start_time: combinedDueDate,
            end_time: endTime.toISOString(),
            all_day: false,
            location: ''
          } as any, { activityId: data.id, targetUserId: msTargetUserId });

          if (msResult?.eventId) {
            await supabase
              .from('sales_activities')
              .update({ microsoft_event_id: msResult.eventId })
              .eq('id', data.id);
          }
        } catch (err) {
          console.error('Failed to sync with Microsoft Calendar (assegnatario):', err);
          // Don't fail the whole operation if calendar sync fails
        }
      }

      toast.success('La task è stata creata con successo');

      // Call the callback to refresh the activities list
      if (onTaskCreated) {
        onTaskCreated();
      }
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        assigned_to: '',
        due_date: '',
        due_time: '09:00',
        type: activityDefaultType
      });
      
      onClose();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Errore nella creazione della task');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        title: '',
        description: '',
        assigned_to: '',
        due_date: '',
        due_time: '09:00',
        type: activityDefaultType
      });
      onClose();
    }
  };

  // Get current date as default minimum
  const today = format(new Date(), 'yyyy-MM-dd');

  // Form content shared between Modal and MobileDrawer
  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          Titolo{activityTitleRequired ? ' *' : ''}
        </label>
        <input
          type="text"
          id="title"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Inserisci il titolo della task..."
          required={activityTitleRequired}
        />
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          Descrizione
        </label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Aggiungi una descrizione..."
        />
      </div>

      {/* Type */}
      <div>
        <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          Tipo
        </label>
        <select
          id="type"
          value={formData.type}
          onChange={(e) => {
            const newType = e.target.value;
            setFormData(prev => ({ ...prev, type: newType as any }));
          }}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="task">Task</option>
          <option value="call">Chiamata</option>
          <option value="videochiamata">Videochiamata</option>
          <option value="appuntamento">Appuntamento fisico</option>
          <option value="sopralluogo">Sopralluogo</option>
          <option value="email">Email</option>
          <option value="other">Altro</option>
          {activeCustomTypes.length > 0 && (
            <>
              <option disabled>──── Tipi personalizzati ────</option>
              {activeCustomTypes.map(customType => (
                <option key={customType.slug} value={customType.slug}>
                  {customType.name}
                </option>
              ))}
            </>
          )}
        </select>
      </div>

      {/* Assigned To */}
      <div>
        <label htmlFor="assigned_to" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          <User className="inline h-4 w-4 mr-1" />
          Assegnata a
        </label>
        <select
          id="assigned_to"
          value={formData.assigned_to}
          onChange={(e) => setFormData(prev => ({ ...prev, assigned_to: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">{booking ? 'Scegli la persona...' : 'Seleziona utente...'}</option>
          {filterBookingOptions(users, booking).map(user => (
            <option key={user.id} value={user.id}>
              {user.full_name || user.email}
            </option>
          ))}
        </select>
        {booking && assigneeError && (
          <p className="mt-1 text-xs text-red-500" role="alert">{assigneeError}</p>
        )}
      </div>

      {/* Date and Time */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="due_date" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            <Calendar className="inline h-4 w-4 mr-1" />
            Data scadenza
          </label>
          <input
            type="date"
            id="due_date"
            value={formData.due_date}
            onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
            min={today}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        <div>
          <label htmlFor="due_time" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            Ora
          </label>
          <input
            type="time"
            id="due_time"
            value={formData.due_time}
            onChange={(e) => setFormData(prev => ({ ...prev, due_time: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* Note */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium">Task indipendente</p>
            <p>Questa task non sarà associata a nessun progetto specifico e sarà visibile nella sezione "Le mie task".</p>
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-600">
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={loading}
        >
          Annulla
        </Button>
        <Button
          type="submit"
          disabled={loading || (activityTitleRequired && !formData.title.trim())}
        >
          {loading ? 'Creazione...' : 'Crea Task'}
        </Button>
      </div>
    </form>
  );

  // Mobile: use MobileDrawer
  if (isMobile) {
    return (
      <MobileDrawer
        isOpen={isOpen}
        onClose={handleClose}
        title="Nuova task"
        height="auto"
        showHandle
        swipeToClose
      >
        <div className="pb-4">
          {formContent}
        </div>
      </MobileDrawer>
    );
  }

  // Desktop: use Modal
  return (
    <ResponsiveModal isOpen={isOpen} onClose={handleClose} title="Nuova task">
      {formContent}
    </ResponsiveModal>
  );
}